import React, { useEffect, useState } from "react";
import { useNavigate, useParams, useLocation } from "react-router-dom";
import { HiArrowLeft } from "react-icons/hi";
import DataTableComponent from "../../../components/Tenant/DataTableComponent/DataTableComponent";
import {
    useGetRuleAnalyticsSummaryMutation,
    useGetRuleAnalyticsUsersMutation,
    useGetRuleAnalyticsTrendMutation,
} from "../../../features/tenant/rules/rulesApi";
import {
    Chart as ChartJS,
    CategoryScale,
    LinearScale,
    PointElement,
    LineElement,
    Title,
    Tooltip,
    Legend,
} from "chart.js";
import { Line } from "react-chartjs-2";

ChartJS.register(
    CategoryScale,
    LinearScale,
    PointElement,
    LineElement,
    Title,
    Tooltip,
    Legend
);

const RuleAnalytics = () => {
    const navigate = useNavigate();
    const { id } = useParams();
    const location = useLocation();
    const ruleName = location.state?.name || `Rule #${id}`;

    // Queries
    const [getSummary, { data: summaryData, isLoading: isSummaryLoading }] = useGetRuleAnalyticsSummaryMutation();
    const [getUsers, { data: usersData, isLoading: isUsersLoading }] = useGetRuleAnalyticsUsersMutation();
    const [getTrend, { data: trendData, isLoading: isTrendLoading }] = useGetRuleAnalyticsTrendMutation();

    const [page, setPage] = useState(1);
    const [limit, setLimit] = useState(10);

    useEffect(() => {
        if (id) {
            const params = {
                id,
                token: sessionStorage.getItem("access_token"),
                tenant_id: JSON.parse(sessionStorage.getItem("vendorData")).tenant_id,
            };

            getSummary(params);
            getTrend(params);
        }
    }, [id]);

    useEffect(() => {
        if (id) {
            getUsers({
                id,
                page,
                limit,
                token: sessionStorage.getItem("access_token"),
                tenant_id: JSON.parse(sessionStorage.getItem("vendorData")).tenant_id,
            });
        }
    }, [id, page, limit]);

    // Columns for User Table
    const columns = [
        {
            name: "User ID",
            cell: (row) => row.user_id,
            sortable: true,
        },
        {
            name: "Name ",
            cell: (row) => row.name,
            sortable: true,
        },
        {
            name: "Mobile ",
            cell: (row) => row.mobile,
            sortable: true,
        },
        {
            name: "State",
            cell: (row) => row.state,
            sortable: true,
        },
        {
            name: "Trigger Count",
            cell: (row) => row.trigger_count,
            sortable: true,
        },
        {
            name: "Total Earned",
            cell: (row) => row.total_earned || "0.00",
            sortable: true,
        },
        {
            name: "Last Reward",
            cell: (row) => row.last_reward_at ? new Date(row.last_reward_at).toLocaleString() : "N/A",
            sortable: true,
        },
    ];

    const handlePageChange = (newPage) => {
        setPage(newPage);
    };

    const handlePerRowsChange = (newLimit) => {
        setLimit(newLimit);
        setPage(1);
    };

    const impact = summaryData?.body?.impact || {};
    const trends = trendData?.body || [];

    // Chart Data
    const chartData = {
        labels: trends.map(t => t.date), // Date string from API
        datasets: [
            {
                label: 'Reward Amount',
                data: trends.map(t => Number(t.amount)),
                borderColor: 'rgb(16, 185, 129)', // Emerald 500
                backgroundColor: 'rgba(16, 185, 129, 0.5)',
                yAxisID: 'y',
                tension: 0.3,
            },
            {
                label: 'Transaction Count',
                data: trends.map(t => Number(t.count)),
                borderColor: 'rgb(59, 130, 246)', // Blue 500
                backgroundColor: 'rgba(59, 130, 246, 0.5)',
                yAxisID: 'y1',
                tension: 0.3,
            },
        ],
    };

    const chartOptions = {
        responsive: true,
        maintainAspectRatio: false,
        interaction: {
            mode: 'index',
            intersect: false,
        },
        stacked: false,
        plugins: {
            title: {
                display: false,
                text: 'Rule Performance Trend',
            },
        },
        scales: {
            y: {
                type: 'linear',
                display: true,
                position: 'left',
                title: { display: true, text: 'Amount' }
            },
            y1: {
                type: 'linear',
                display: true,
                position: 'right',
                grid: {
                    drawOnChartArea: false,
                },
                title: { display: true, text: 'Count' }
            },
        },
    };

    return (
        <div className="bg-gray-50 min-h-screen max-w-8xl mx-2 my-1 p-2">
            <div className="max-w-full mx-auto space-y-6">

                {/* Header */}
                <div className="bg-white shadow-sm rounded-xl p-6 flex items-center gap-4">
                    <button
                        onClick={() => navigate(-1)}
                        className="p-2 rounded-full hover:bg-gray-100 transition-colors text-gray-500 hover:text-gray-700"
                    >
                        <HiArrowLeft className="w-6 h-6" />
                    </button>
                    <div>
                        <h1 className="text-2xl font-bold text-gray-900">Rule Analytics</h1>
                        <p className="text-gray-500">{ruleName}</p>
                    </div>
                </div>

                {/* KPI Cards */}
                <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                    <KpiCard
                        title="Unique Users"
                        value={impact.uniqueUsers || 0}
                        loading={isSummaryLoading}
                        color="blue"
                    />
                    <KpiCard
                        title="Total Triggers"
                        value={impact.totalTriggers || 0}
                        loading={isSummaryLoading}
                        color="indigo"
                    />
                    <KpiCard
                        title="Total Value Distributed"
                        value={impact.totalValueDistributed || 0}
                        loading={isSummaryLoading}
                        color="emerald"
                    />
                </div>

                {/* Trend Chart */}
                <div className="bg-white shadow-sm rounded-xl p-6">
                    <h2 className="text-lg font-semibold text-gray-900 mb-6">Performance Trend (Last 30 Days)</h2>
                    <div className="h-80 w-full">
                        {isTrendLoading ? (
                            <div className="h-full flex items-center justify-center">
                                <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-blue-500"></div>
                            </div>
                        ) : trends.length > 0 ? (
                            <Line options={chartOptions} data={chartData} />
                        ) : (
                            <div className="h-full flex items-center justify-center text-gray-400 italic">
                                No trend data available for this period.
                            </div>
                        )}
                    </div>
                </div>

                {/* User List Table */}
                <div className="bg-white shadow-sm rounded-xl p-6">
                    <h2 className="text-lg font-semibold text-gray-900 mb-4">User Leaderboard (Top Earners)</h2>
                    <DataTableComponent
                        columns={columns}
                        data={usersData?.body?.data || []}
                        loading={isUsersLoading}
                        totalRows={usersData?.body?.meta?.total || 0}
                        handlePerRowsChange={handlePerRowsChange}
                        handlePageChange={handlePageChange}
                    />
                </div>
            </div>
        </div>
    );
};

const KpiCard = ({ title, value, suffix = "", loading, color }) => {
    const colorClasses = {
        blue: "bg-blue-50 text-blue-700 border-blue-200",
        indigo: "bg-indigo-50 text-indigo-700 border-indigo-200",
        emerald: "bg-emerald-50 text-emerald-700 border-emerald-200",
    };

    const baseClass = colorClasses[color] || colorClasses.blue;

    return (
        <div className={`p-6 rounded-xl border shadow-sm bg-white`}>
            <p className="text-sm font-medium text-gray-500 uppercase tracking-wider">{title}</p>
            <div className="mt-2 flex items-baseline">
                {loading ? (
                    <div className="h-8 w-24 bg-gray-200 animate-pulse rounded"></div>
                ) : (
                    <span className={`text-3xl font-extrabold ${baseClass.split(" ")[1]}`}>
                        {/* Parse float if string, then format */}
                        {Number(value).toLocaleString(undefined, { maximumFractionDigits: 2 })}{suffix}
                    </span>
                )}
            </div>
        </div>
    );
};

export default RuleAnalytics;
